import { useState, useEffect } from "react";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";

export default function ContactPage() {
  const [scrolled, setScrolled] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    
    // Check for success parameter from Netlify redirect
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setIsSubmitted(true);
    }
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as any).toString(),
      });
      
      // Show success message without page reload
      setIsSubmitted(true);
      window.history.replaceState({}, '', '/contact?success=true');
    } catch (error) {
      console.error("Form submission error:", error);
      alert("There was an error submitting the form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    // Clear URL params and reset form
    window.history.replaceState({}, '', '/contact');
    setIsSubmitted(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* Hero Banner */}
      <div className="relative pt-20 bg-gradient-to-br from-[#144E5D] to-[#0f3a47] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#B8D8DE] rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-24 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 animate-fade-in">
            <i className="ri-customer-service-2-line text-[#B8D8DE]"></i>
            <span className="text-sm text-[#B8D8DE] font-medium">We're here to help</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Get in Touch
          </h1>
          <p className="text-lg text-gray-300 max-w-xl mx-auto">
            Your thoughts, questions, and feedback are invaluable to us. Our team is ready to provide a prompt and personalized response.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
      </div>

      {/* Contact Info Cards */}
      <div className="max-w-5xl mx-auto px-6 -mt-8 relative z-10 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="group bg-white rounded-xl shadow-lg hover:shadow-xl p-6 border border-gray-100 hover:border-[#144E5D]/20 transition-all duration-300 hover:-translate-y-1">
            <div className="w-12 h-12 bg-[#144E5D]/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-[#144E5D] transition-all duration-300">
              <i className="ri-map-pin-line text-xl text-[#144E5D] group-hover:text-white transition-all duration-300"></i>
            </div>
            <h3 className="text-base font-semibold text-gray-800 mb-2">Visit Us</h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              333B US 46 W, Unit 140<br />
              Fairfield, NJ 07004
            </p>
          </div>
          <div className="group bg-white rounded-xl shadow-lg hover:shadow-xl p-6 border border-gray-100 hover:border-[#144E5D]/20 transition-all duration-300 hover:-translate-y-1">
            <div className="w-12 h-12 bg-[#144E5D]/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-[#144E5D] transition-all duration-300">
              <i className="ri-mail-line text-xl text-[#144E5D] group-hover:text-white transition-all duration-300"></i>
            </div>
            <h3 className="text-base font-semibold text-gray-800 mb-2">Email Us</h3>
            <a
              href="mailto:info@mycurewell.com"
              className="text-sm text-[#144E5D] hover:underline font-medium"
            >
              info@mycurewell.com
            </a>
          </div>
          <div className="group bg-white rounded-xl shadow-lg hover:shadow-xl p-6 border border-gray-100 hover:border-[#144E5D]/20 transition-all duration-300 hover:-translate-y-1">
            <div className="w-12 h-12 bg-[#144E5D]/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-[#144E5D] transition-all duration-300">
              <i className="ri-phone-line text-xl text-[#144E5D] group-hover:text-white transition-all duration-300"></i>
            </div>
            <h3 className="text-base font-semibold text-gray-800 mb-2">Call Us</h3>
            <a
              href="tel:+18453670500"
              className="text-sm text-[#144E5D] hover:underline font-medium"
            >
              +1 (845) 367-0500
            </a>
          </div>
        </div>
      </div>

      {/* Contact Form / Success Message */}
      <div className="max-w-3xl mx-auto px-6 pb-20">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12">
          {!isSubmitted ? (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Send Us a Message</h2>
                <p className="text-gray-500 text-sm">Fill out the form below and we'll get back to you as soon as possible.</p>
              </div>

              <form
                name="contact"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden">
                  <label>
                    Don't fill this out if you're human: <input name="bot-field" />
                  </label>
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-1.5"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#144E5D] focus:border-transparent outline-none transition-all bg-gray-50 hover:bg-white disabled:opacity-50"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1.5"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#144E5D] focus:border-transparent outline-none transition-all bg-gray-50 hover:bg-white disabled:opacity-50"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#144E5D] focus:border-transparent outline-none transition-all bg-gray-50 hover:bg-white"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#144E5D] focus:border-transparent outline-none transition-all resize-vertical bg-gray-50 hover:bg-white"
                    placeholder="Tell us more..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full md:w-auto px-10 py-3.5 bg-[#144E5D] text-white font-medium rounded-xl hover:bg-[#0f3a47] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer inline-flex items-center justify-center gap-2"
                >
                  Send Message
                  <i className="ri-send-plane-line"></i>
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-8 animate-fade-in">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-checkbox-circle-line text-4xl text-green-600"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">Message Sent Successfully!</h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Thank you for contacting us. We've received your message and will get back to you as soon as possible.
              </p>
              <button
                onClick={handleReset}
                className="px-8 py-3 bg-[#144E5D] text-white font-medium rounded-xl hover:bg-[#0f3a47] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 inline-flex items-center gap-2"
              >
                <i className="ri-mail-send-line"></i>
                Send Another Message
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
